import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client";
import { useEvent } from "../context/EventProvider";
import { useT } from "../settings/context";

/**
 * ═══ LES RÉACTIONS ═══
 *
 * ⚠️ UNE COUCHE, PAS UN MODULE. La V1 en fait un écran à part (`/live/emotions`)
 * et un second websocket, alors qu'elle les colle DÉJÀ sous chacun de ses modules
 * (`<UserEmotions/>` dans `live.tsx`) : elles accompagnent ce qui se passe, elles
 * ne le remplacent pas. Ici on peut réagir pendant n'importe quelle activité, et
 * même quand il n'y en a aucune.
 *
 * ⚠️ ELLES NE S'AFFICHENT QUE SI UNE SÉQUENCE EST OUVERTE. C'est l'organisateur
 * qui en ouvre une depuis le back-office ; sans séquence, un clic n'a nulle part
 * où être rangé. Des boutons qui ne font rien sont pires que pas de boutons.
 */

type Emotion = { id: number; label: string; iconUrl: string };

export function Reactions({ sequenceId }: { sequenceId: number | null }) {
  const event = useEvent();
  const t = useT();

  const { data: emotions } = useQuery({
    queryKey: ["emotions", event?.domainId, event?.eventId],
    queryFn: ({ signal }) =>
      apiFetch<Emotion[]>(
        `/domains/${event!.domainId}/events/${event!.eventId}/emotions`,
        { api: "apix", signal },
      ),
    enabled: !!event && !!sequenceId,
    retry: false,
  });

  const react = useMutation({
    mutationFn: (emotionId: number) =>
      apiFetch(
        `/domains/${event!.domainId}/events/${event!.eventId}/emotions/users`,
        { api: "apix", method: "POST", body: { emotionId, sequenceId } },
      ),
  });

  if (!sequenceId || !emotions?.length) return null;

  return (
    <div className="mod" data-mod="emotions">
      <b>{t("LIVE_REACT_NOW", "Réagissez en direct")}</b>
      <div className="emo">
        {emotions.map((emotion) => (
          <button
            key={emotion.id}
            type="button"
            /* Le clic part et on n'attend pas : une réaction est un geste, pas une
               transaction. La bloquer le temps d'un aller-retour la rendrait molle,
               et c'est exactement ce qu'on ne veut pas d'un mur de réactions. */
            onClick={() => react.mutate(emotion.id)}
          >
            <img src={emotion.iconUrl} alt={emotion.label} />
          </button>
        ))}
      </div>
    </div>
  );
}
